import { formatYmdLocal } from "@/lib/utils/date/formatYmdLocal";
import {
  getStudioClock,
  studioTodayDate,
  studioTodayYmd,
} from "@/lib/utils/date/studio-clock";
import type { GetClassResponse } from "@/openapi/generated/models";
import { getPrimaryClassTeacherId } from "./classAttendanceTeacher";

const YMD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** JS getDay(): 0=Sun … 6=Sat - matches API DayOfWeek strings */
const DAY_NAME_TO_INDEX: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

export function parseAttendanceSessionDateParam(
  param: string | null | undefined,
  maxYmd: string = studioTodayYmd(),
): string | null {
  if (!param || !YMD_PATTERN.test(param)) return null;
  const [y, m, d] = param.split("-").map(Number);
  const parsed = new Date(y, m - 1, d, 12, 0, 0, 0);
  if (
    parsed.getFullYear() !== y ||
    parsed.getMonth() !== m - 1 ||
    parsed.getDate() !== d
  ) {
    return null;
  }
  if (param > maxYmd) return maxYmd;
  return param;
}

export function clampAttendanceSessionDateToToday(
  ymd: string,
  refDate: Date = new Date(),
): string {
  const today = studioTodayYmd(refDate);
  return ymd > today ? today : ymd;
}

/** Most recent calendar date ≤ refDate that falls on a scheduled weekday (never in the future). */
export function getLastScheduledSessionYmd(
  scheduledDayNames: string[],
  refDate: Date = new Date(),
): string {
  const clock = getStudioClock(refDate);
  const todayYmd = clock.ymd;
  if (!scheduledDayNames || scheduledDayNames.length === 0) {
    return todayYmd;
  }
  const allowed = new Set(
    scheduledDayNames
      .map((n) => DAY_NAME_TO_INDEX[n])
      .filter((v) => v !== undefined),
  );
  if (allowed.size === 0) return todayYmd;

  const d = studioTodayDate(clock);
  d.setHours(12, 0, 0, 0);
  for (let i = 0; i < 370; i++) {
    if (allowed.has(d.getDay())) {
      return formatYmdLocal(d);
    }
    d.setDate(d.getDate() - 1);
  }
  return todayYmd;
}

export function isScheduledClassDay(
  dateYmd: string,
  scheduledDayNames: string[],
): boolean {
  if (scheduledDayNames.length === 0) return true;
  const d = new Date(`${dateYmd}T12:00:00`);
  const weekday = d.getDay();
  return scheduledDayNames.some((name) => DAY_NAME_TO_INDEX[name] === weekday);
}

export function getScheduleDayNamesFromClass(
  classDetail: GetClassResponse | undefined | null,
  fallbackDay?: string | null,
): string[] {
  const fromDetail = (classDetail?.classDays ?? [])
    .map((cd) => cd.day)
    .filter(Boolean) as string[];
  if (fromDetail.length > 0) return fromDetail;
  if (fallbackDay?.trim()) return [fallbackDay.trim()];
  return [];
}

/** URL `date` wins when valid; otherwise last scheduled session on or before today. */
export function resolveAttendanceSessionDate(
  scheduleDayNames: string[],
  urlDateParam: string | null | undefined,
  refDate: Date = new Date(),
): string {
  const today = studioTodayYmd(refDate);
  const fromUrl = parseAttendanceSessionDateParam(urlDateParam, today);
  if (fromUrl) return fromUrl;
  return clampAttendanceSessionDateToToday(
    getLastScheduledSessionYmd(scheduleDayNames, refDate),
    refDate,
  );
}

export function buildAttendanceClassUrl(
  classId: number,
  sessionDateYmd: string,
): string {
  const params = new URLSearchParams({ date: sessionDateYmd });
  return `/admin/dashboard/attendance/${classId}?${params.toString()}`;
}

/** Studio desk link into the teacher attendance session for this class. */
export function buildClassAttendanceHref(
  classId: number,
  classDetail: GetClassResponse | null | undefined,
): string | null {
  if (getPrimaryClassTeacherId(classDetail) == null) return null;
  return buildAttendanceClassUrl(
    classId,
    resolveAttendanceSessionDate(getScheduleDayNamesFromClass(classDetail), null),
  );
}
